import React, { useState, useEffect } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonButtons,
  IonMenuButton,
  IonLoading,
} from "@ionic/react";
import { useHistory, useRouteMatch } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Grid,
  Paper,
  Typography,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import { formatCurrencyN } from "../../../../common/utils_t";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { useForm, Controller } from "react-hook-form";
import { assetSchemaResolver } from "../../../../api/form-validations/assets";
import { assetCategories, assetConditions } from "../../../../common/consts";
import { useAssets } from "../../../../hooks/useAssets";
import { Asset } from "../../../../models/asset.types";
// import { useAssets } from "../../hooks/useAssets";
// import { Asset } from "../../types/asset.types";
// import { assetSchemaResolver } from "../../services/form-validations/assets";
// import { assetCategories, assetConditions } from "../../utils/consts";

const EditAsset: React.FC = () => {
  const history = useHistory();
  const match = useRouteMatch<{ id: string }>(
    "/core-accounting/asset-register/:id/edit"
  );
  const asset_id = match?.params?.id || "";
  const { asset, updateAsset, loading, fetchAsset, calculateDepreciation } =
    useAssets();
  const [purchaseDate, setPurchaseDate] = useState<Dayjs | null>(null);
  const [warrantyExpiration, setWarrantyExpiration] = useState<Dayjs | null>(
    null
  );

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<Partial<Asset>>({
    resolver: assetSchemaResolver,
  });

  // Watch quantity, unit_value, and life_span to auto-calculate value and depreciation
  const quantity = watch("quantity") || 1;
  const unitValue = watch("unit_value") || 0;
  const lifeSpan = watch("life_span") || 0;

  // Calculate total value
  const totalValue = quantity * unitValue;

  // Calculate depreciation
  const depreciationValue =
    unitValue && lifeSpan ? calculateDepreciation(unitValue, lifeSpan) : 0;

  useEffect(() => {
    setValue("value", totalValue);
    if (depreciationValue > 0) {
      setValue("depreciation_value", depreciationValue);
    }
  }, [totalValue, depreciationValue, setValue]);

  // Fetch asset data on mount
  useEffect(() => {
    if (asset_id) {
      fetchAsset(asset_id);
    }
  }, [asset_id]);

  // Populate form when asset is fetched
  useEffect(() => {
    if (asset) {
      setValue("name", asset.name || "");
      setValue("description", asset.description || "");
      setValue("id_tag", asset.id_tag || "");
      setValue("category_id", asset.category_id || "");
      setValue("dept_area", asset.dept_area || "");
      setValue("room", asset.room || "");
      setValue("supplier", asset.supplier || "");
      setValue("life_span", asset.life_span || undefined);
      setValue("depreciation_value", asset.depreciation_value || undefined);
      setValue("asset_condition", asset.asset_condition || "");
      setValue("unit_value", asset.unit_value || 0);
      setValue("quantity", asset.quantity || 1);
      setValue("value", asset.value || 0);
      setValue("purchase_date", asset.purchase_date || "");
      setValue("warranty_expiration", asset.warranty_expiration || undefined);

      // Set date picker values
      if (asset.purchase_date) {
        setPurchaseDate(dayjs(asset.purchase_date));
      }
      if (asset.warranty_expiration) {
        setWarrantyExpiration(dayjs(asset.warranty_expiration));
      }
    }
  }, [asset, setValue]);

  const onSubmit = async (data: Partial<Asset>) => {
    if (!asset_id) return;

    const assetData: Partial<Asset> = {
      ...data,
      value: totalValue,
      depreciation_value: depreciationValue > 0 ? depreciationValue : undefined,
      purchase_date: purchaseDate?.format("YYYY-MM-DD") || "",
      warranty_expiration:
        warrantyExpiration?.format("YYYY-MM-DD") || undefined,
    };

    await updateAsset(asset_id, assetData);
    history.push("/core-accounting/asset-register");
  };

  if (loading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonMenuButton />
            </IonButtons>
            <IonTitle>Edit Asset</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <IonLoading isOpen={loading} message="Loading asset..." />
          </Box>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Edit Asset</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => history.goBack()}>Cancel</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <Box sx={{ p: 2 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Asset Information
            </Typography>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                {/* Basic Information */}
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    sx={{ mt: 2, mb: 1, fontWeight: "bold" }}
                  >
                    Basic Information
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Asset Name *"
                    {...register("name")}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="ID Tag"
                    {...register("id_tag")}
                    error={!!errors.id_tag}
                    helperText={errors.id_tag?.message}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Description"
                    {...register("description")}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Category"
                    {...register("category_id")}
                    error={!!errors.category_id}
                    helperText={errors.category_id?.message}
                    defaultValue={asset?.category_id || ""}
                  >
                    <MenuItem value={asset?.category_id || ""}>
                      Select Category
                    </MenuItem>
                    {assetCategories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Condition"
                    {...register("asset_condition")}
                    error={!!errors.asset_condition}
                    helperText={errors.asset_condition?.message}
                    defaultValue={asset?.asset_condition || ""}
                  >
                    <MenuItem value={asset?.asset_condition || ""}>
                      Select Condition
                    </MenuItem>
                    {assetConditions.map((condition) => (
                      <MenuItem key={condition.id} value={condition.name}>
                        {condition.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Location Information */}
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    sx={{ mt: 2, mb: 1, fontWeight: "bold" }}
                  >
                    Location Information
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Department/Area"
                    {...register("dept_area")}
                    error={!!errors.dept_area}
                    helperText={errors.dept_area?.message}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Room"
                    {...register("room")}
                    error={!!errors.room}
                    helperText={errors.room?.message}
                  />
                </Grid>

                {/* Purchase Information */}
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    sx={{ mt: 2, mb: 1, fontWeight: "bold" }}
                  >
                    Purchase Information
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="purchase_date"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        label="Purchase Date *"
                        value={purchaseDate}
                        onChange={(newValue) => {
                          setPurchaseDate(newValue);
                          field.onChange(newValue?.format("YYYY-MM-DD") || "");
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.purchase_date,
                            helperText: errors.purchase_date?.message,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Supplier"
                    {...register("supplier")}
                    error={!!errors.supplier}
                    helperText={errors.supplier?.message}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="warranty_expiration"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        label="Warranty Expiration"
                        value={warrantyExpiration}
                        onChange={(newValue) => {
                          setWarrantyExpiration(newValue);
                          field.onChange(newValue?.format("YYYY-MM-DD") || "");
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.warranty_expiration,
                            helperText: errors.warranty_expiration?.message,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Life Span (Years)"
                    {...register("life_span", { valueAsNumber: true })}
                    error={!!errors.life_span}
                    helperText={errors.life_span?.message}
                  />
                </Grid>

                {/* Financial Information */}
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    sx={{ mt: 2, mb: 1, fontWeight: "bold" }}
                  >
                    Financial Information
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Unit Value *"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">₦</InputAdornment>
                      ),
                    }}
                    {...register("unit_value", { valueAsNumber: true })}
                    error={!!errors.unit_value}
                    helperText={errors.unit_value?.message}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Quantity *"
                    {...register("quantity", { valueAsNumber: true })}
                    error={!!errors.quantity}
                    helperText={errors.quantity?.message}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ pt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Total Asset Value
                    </Typography>
                    <Typography
                      variant="h6"
                      color="primary"
                      sx={{ fontWeight: "bold" }}
                    >
                      {formatCurrencyN(totalValue)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Automatically calculated (Quantity × Unit Value)
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ pt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Depreciation Value
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      {depreciationValue > 0
                        ? formatCurrencyN(depreciationValue)
                        : "N/A"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {lifeSpan > 0
                        ? `Automatically calculated (Unit Value ÷ Life Span)`
                        : "Enter Life Span to calculate"}
                    </Typography>
                  </Box>
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      justifyContent: "flex-end",
                      mt: 2,
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => history.goBack()}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={loading}
                    >
                      {loading ? "Updating..." : "Update Asset"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Box>

        <IonLoading isOpen={loading} message="Updating asset..." />
      </IonContent>
    </IonPage>
  );
};

export default EditAsset;
